package org.vreo.devicehub.features.device.presentation.registerDevice

import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import org.vreo.devicehub.features.core.domain.DataError
import org.vreo.devicehub.features.core.domain.Result
import org.vreo.devicehub.features.core.domain.onError
import org.vreo.devicehub.features.core.domain.onSuccess
import org.vreo.devicehub.features.core.presentation.stingInputToFloatFormatter.validateAndFormatAmount
import org.vreo.devicehub.features.core.presentation.toUiText

import org.vreo.devicehub.features.device.domain.DeviceAccessRepository
import org.vreo.devicehub.features.core.domain.device.device.AnprDevice
import org.vreo.devicehub.features.core.domain.device.device.ProductType

/**
 * Register device view model, responsible for registering new device with backend services. Only
 * for production and development use.
 *
 * @property authenticationToken user authentication token
 * @property repository repository for accessing device APIs
 */
class RegisterDeviceViewModel(
    val authenticationToken: String,
    val repository: DeviceAccessRepository
) : ViewModel() {
    private val _state = MutableStateFlow(RegisterDeviceState())
    val state = _state.asStateFlow()

    // Minimal required serial number length
    private val miniSerialNumberLength: Int = 4

    /**
     * On action handler
     *
     * @param action action to be handled
     */
    fun onAction(action: RegisterDeviceActions) {
        when (action) {
            is RegisterDeviceActions.OnClearError -> {
                _state.update {
                    it.copy(
                        error = null
                    )
                }
            }

            is RegisterDeviceActions.OnBackNavigation -> {}

            is RegisterDeviceActions.OnRegisterDeviceClicked -> {
                registerDevice(action.callback)
            }

            is RegisterDeviceActions.OnConnectToDeviceClicked -> {
                accessDevice()
            }

            is RegisterDeviceActions.OnUpdateSelectedProduct -> {
                _state.update {
                    it.copy(
                        selectedProductIndex = action.index
                    )
                }
            }

            is RegisterDeviceActions.OnUpdateSerialNumber -> {
                _state.update {
                    it.copy(
                        serialNumber = validateAndFormatAmount(action.value.lowercase()),
                        invalidSerialNumber = action.value.length < miniSerialNumberLength
                    )
                }
            }

            is RegisterDeviceActions.OnUpdateAutoGenerateSerialNumber -> {
                if (action.value) {
                    _state.update {
                        it.copy(
                            autoGenerateSerialNumber = action.value,
                            invalidSerialNumber = false
                        )
                    }
                } else {
                    _state.update {
                        it.copy(
                            autoGenerateSerialNumber = action.value,
                        )
                    }
                }
            }

            is RegisterDeviceActions.OnUpdateDeviceIpAddress -> {
                _state.update {
                    it.copy(
                        deviceIpAddress = action.value
                    )
                }
            }
        }
    }

    /**
     * Connect to device on local network
     */
    private fun accessDevice() = viewModelScope.launch {
        _state.update {
            it.copy(
                isLoading = true
            )
        }

        runCatching {
            val result =
                repository.connectToLocalDevice(deviceIpAddress = _state.value.deviceIpAddress)
            result
                .onSuccess {
                    _state.update {
                        it.copy(
                            isLoading = false,
                            error = null,
                            deviceConnected = true
                        )
                    }
                }
                .onError { error ->
                    _state.update {
                        it.copy(
                            isLoading = false,
                            error = error.toUiText(),
                            deviceConnected = false
                        )
                    }
                }
        }.getOrElse {
            _state.update {
                it.copy(
                    isLoading = false,
                    error = DataError.Remote.UNKNOWN.toUiText()
                )
            }
        }
    }

    /**
     * Register device to production
     *
     * @param callback callback to be called on successful register device operation
     */
    private fun registerDevice(callback: (() -> Unit)?) = viewModelScope.launch {
        // Don't perform device registration if devic eis not connected
        if (!_state.value.deviceConnected) {
            _state.update {
                it.copy(
                    isLoading = false,
                    error = DataError.Remote.INVALID_ARGUMENT.toUiText()
                )
            }
            return@launch
        }

        _state.update {
            it.copy(
                isLoading = true
            )
        }

        runCatching {
            registerDeviceWithBackend()
                .onSuccess { data ->
                    // Display registered device information
                    _state.update { it.copy(device = data) }

                    // Send data to device
                    repository.applyDeviceConfiguration(
                        deviceIpAddress = _state.value.deviceIpAddress,
                        device = data
                    )
                        .onSuccess {
                            _state.update {
                                it.copy(
                                    isLoading = false,
                                    error = null,
                                    deviceConnected = false
                                )
                            }
                            callback?.invoke()
                        }
                        .onError { error ->
                            _state.update {
                                it.copy(
                                    isLoading = false,
                                    error = error.toUiText(),
                                    deviceConnected = false
                                )
                            }
                        }

                }
                .onError { error ->
                    _state.update {
                        it.copy(
                            isLoading = false,
                            error = error.toUiText()
                        )
                    }
                }
        }.getOrElse {
            _state.update {
                it.copy(
                    isLoading = false,
                    error = DataError.Remote.UNKNOWN.toUiText()
                )
            }
        }
    }

    /**
     * Register device with backend services
     *
     * @return Result of registered device on success or error on failure
     */
    private suspend fun registerDeviceWithBackend(): Result<AnprDevice, DataError.Remote> {
        // Verify serial number
        val serialNumber = if (_state.value.autoGenerateSerialNumber) {
            null
        } else {
            if (_state.value.invalidSerialNumber) {
                return Result.Error(DataError.Remote.INVALID_ARGUMENT)
            } else {
                _state.value.serialNumber
            }
        }

        // Get selected product/device type
        val productType = ProductType.VALUES[state.value.selectedProductIndex]

        // Register device
        return repository.registerNewDevice(
            token = authenticationToken,
            productNumber = productType.productNumber,
            serialNumber = serialNumber?.toLong()
        )
    }


}